import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Send, Plus, Trash2 } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { PeppolIngestDrawer, PeppolSendDrawer } from '../../components/PeppolDrawers';
import { IntroCallout } from '../../components/ui/IntroCallout';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { useConfirm } from '../../components/ui/ConfirmDialog';

interface PeppolMessage {
  id: string;
  direction: 'in' | 'out';
  message_id: string;
  document_type: string | null;
  status: string;
  received_at: string | null;
}

interface PeppolRegistration {
  id: string;
  scheme: string;
  identifier: string;
  provider: string | null;
  smp_status: 'pending' | 'registered' | 'failed';
}

function RegistrationsCard({ wsId }: { wsId: string }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [scheme, setScheme] = useState('0188');
  const [identifier, setIdentifier] = useState('');
  const [error, setError] = useState<string | null>(null);
  const regs = useQuery<PeppolRegistration[]>({
    queryKey: ['peppol-ap-regs', wsId],
    queryFn: async () => (await api.get<PeppolRegistration[]>(`/workspaces/${wsId}/peppol-ap/registrations`)).data,
  });
  const register = useMutation({
    mutationFn: async () => (await api.post(`/workspaces/${wsId}/peppol-ap/register`, { scheme, identifier })).data,
    onSuccess: () => { setIdentifier(''); setError(null); qc.invalidateQueries({ queryKey: ['peppol-ap-regs', wsId] }); },
    onError: (err: any) => setError(err?.response?.data?.message ?? err?.message ?? 'Failed'),
  });
  const del = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/workspaces/${wsId}/peppol-ap/registrations/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['peppol-ap-regs', wsId] }),
  });
  const inputCn = 'h-9 px-2 rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 text-sm';
  const tone = (s: string) => (s === 'registered' ? 'success' : s === 'failed' ? 'warn' : 'accent');
  return (
    <Card className="mb-5">
      <div className="px-5 py-3 border-b border-ink-200 dark:border-ink-800 font-medium">{t('peppol.registrations')}</div>
      <div className="p-4 flex items-end gap-2 flex-wrap border-b border-ink-200 dark:border-ink-800">
        <div>
          <label className="text-[11px] uppercase tracking-wide text-ink-500 block mb-1">{t('peppol.scheme')}</label>
          <input value={scheme} onChange={(e) => setScheme(e.target.value)} placeholder="0188" className={`${inputCn} w-24`} />
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="text-[11px] uppercase tracking-wide text-ink-500 block mb-1">{t('peppol.identifier')}</label>
          <input value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="参加者ID / participant id" className={`${inputCn} w-full`} />
        </div>
        <button type="button" onClick={() => register.mutate()} disabled={register.isPending || !identifier.trim()} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50">
          <Plus size={12} /> {t('peppol.register')}
        </button>
      </div>
      {error && <div className="px-4 py-2 text-xs text-warn-600">{error}</div>}
      {regs.data && regs.data.length > 0 ? (
        <ul className="divide-y divide-ink-200 dark:divide-ink-800">
          {regs.data.map((r) => (
            <li key={r.id} className="px-5 py-2.5 flex items-center justify-between text-sm">
              <span className="num">{r.scheme}:{r.identifier}{r.provider ? ` · ${r.provider}` : ''}</span>
              <div className="flex items-center gap-2">
                <Pill tone={tone(r.smp_status)}>{t(`peppol.smpStatus.${r.smp_status}`)}</Pill>
                <button
                  type="button"
                  onClick={async () => { if (await confirm({ title: t('common.confirmDelete'), confirmLabel: t('common.delete'), destructive: true })) del.mutate(r.id); }}
                  className="p-1.5 rounded-md text-ink-400 hover:text-warn-600"
                  title={t('common.delete')}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="px-5 py-4 text-xs text-ink-500">{t('peppol.noRegistrations')}</div>
      )}
    </Card>
  );
}

export function PeppolPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const [direction, setDirection] = useState<'' | 'in' | 'out'>('');
  const [sendOpen, setSendOpen] = useState(false);
  const [ingestOpen, setIngestOpen] = useState(false);
  const { data, isLoading } = useQuery<PeppolMessage[]>({
    queryKey: ['peppol', wsId, direction],
    enabled: !!wsId,
    queryFn: async () =>
      (await api.get(`/workspaces/${wsId}/peppol${direction ? `?direction=${direction}` : ''}`)).data,
  });

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-6xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="display text-2xl font-semibold">{t('peppol.title')}</h1>
            <p className="text-sm text-ink-500 mt-1">{t('peppol.subtitle')}</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={direction}
              onChange={(e) => setDirection(e.target.value as '' | 'in' | 'out')}
              className="h-9 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 text-sm"
            >
              <option value="">{t('common.all')}</option>
              <option value="in">{t('peppol.inbound')}</option>
              <option value="out">{t('peppol.outbound')}</option>
            </select>
            <button
              type="button"
              onClick={() => setIngestOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-ink-300 dark:border-ink-700 hover:bg-ink-50 dark:hover:bg-ink-900 text-sm font-medium"
            >
              <Download size={14} />
              {t('peppol.ingestUbl')}
            </button>
            <button
              type="button"
              onClick={() => setSendOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-foreground text-background hover:bg-foreground/90 text-sm font-medium"
            >
              <Send size={14} />
              {t('peppol.sendInvoice')}
            </button>
          </div>
        </div>

        <IntroCallout storageKey="peppol" title={t('intro.peppol.title')} body={t('intro.peppol.body')} />

        {wsId && <RegistrationsCard wsId={wsId} />}

        <Card>
          {isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : !data || data.length === 0 ? (
            <div className="p-10 text-center text-ink-500 text-sm">{t('peppol.empty')}</div>
          ) : (
            <ul className="divide-y divide-ink-200 dark:divide-ink-800">
              {data.map((m) => (
                <li key={m.id} className="px-5 py-3 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-mono text-xs text-ink-400">{m.message_id}</div>
                    <div className="text-xs text-ink-500 mt-1">{m.document_type}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Pill tone={m.direction === 'in' ? 'accent' : 'neutral'}>
                      {m.direction === 'in' ? t('peppol.inbound') : t('peppol.outbound')}
                    </Pill>
                    <Pill tone={m.status === 'sent' || m.status === 'linked' ? 'success' : m.status === 'rejected' ? 'warn' : 'neutral'}>
                      {m.status}
                    </Pill>
                    {m.received_at && (
                      <span className="text-xs text-ink-500 num">
                        {formatDate(m.received_at, i18n.language)}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {wsId && (
        <>
          <PeppolSendDrawer wsId={wsId} open={sendOpen} onOpenChange={setSendOpen} />
          <PeppolIngestDrawer wsId={wsId} open={ingestOpen} onOpenChange={setIngestOpen} />
        </>
      )}
    </AppShell>
  );
}
